import React from 'react';
import AnimatedModal from '../../components/ui/AnimatedModal';
import { UserIcon, UsersIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Small confirmation modal shown after "Apply columns". Lets the recruiter choose
 * whether the new column order/visibility is saved:
 *   - personal ("Just for me")            -> follows this user across devices
 *   - shared   ("For everyone on this      -> every user who opens this screening
 *                screening")                  sees it (takes precedence over personal)
 *
 * onConfirm(scope) is called with 'personal' | 'shared'.
 */
export default function ColumnSaveScopeModal({ isOpen, onClose, onConfirm, saving = false }) {
  const [choice, setChoice] = React.useState('shared');

  React.useEffect(() => {
    if (isOpen) setChoice('shared');
  }, [isOpen]);

  const options = [
    {
      id: 'shared',
      label: 'For everyone on this screening',
      description: 'Everyone who opens this screening sees this column order. Overrides personal layouts.',
      Icon: UsersIcon,
    },
    {
      id: 'personal',
      label: 'Just for me',
      description: 'Only you see this layout. It follows your account on any computer you log in from.',
      Icon: UserIcon,
    },
  ];

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="bg-black/40"
      contentClassName="w-full max-w-md overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
      ariaLabelledBy="column-save-scope-title"
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <h2 id="column-save-scope-title" className="text-base font-semibold tracking-tight text-slate-900">
            Save column order
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-2.5 px-6 py-5">
          <p className="mb-1 text-sm text-slate-500">Who should this column order apply to?</p>
          {options.map(({ id, label, description, Icon }) => {
            const active = choice === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setChoice(id)}
                className={`flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition ${
                  active
                    ? 'border-[#007BFF]/40 bg-[#007BFF]/5 ring-1 ring-[#007BFF]/10'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                    active ? 'bg-[#007BFF] text-white' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{label}</span>
                    <span
                      className={`ml-auto flex h-4 w-4 items-center justify-center rounded-full border ${
                        active ? 'border-[#007BFF] bg-[#007BFF]' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {active && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{description}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.(choice)}
            disabled={saving}
            className="rounded-lg border border-[#007BFF] bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0069d9] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
}
